'use client';

import {
  FeedbackInviteMode,
  type FeedbackFormTemplate,
  type FeedbackQuestion,
} from '@up2stay/up2promo-api-client';
import type { ProductFeedbackQuestionDraft } from '@up2stay/up2promo-commons/products';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@up2stay/up2promo-ui';
import { useMemo, useState } from 'react';

import { UpgradeCard } from '@/core/components/billing/upgrade-surfaces';
import { FeedbackTemplateLibraryPicker } from '@/core/components/feedback/feedback-template-library-picker';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useFeedbackFormTemplates } from '@/core/hooks/use-feedback';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import type { ProductsDict } from '@/routes/app/products/products.types';

export type ProductFeedbackFunnelValues = {
  feedbackMode: FeedbackInviteMode;
  reviewThreshold: number;
  feedbackQuestions: ProductFeedbackQuestionDraft[];
};

type ProductFeedbackFunnelFieldsProps = {
  values: ProductFeedbackFunnelValues;
  onChange: (patch: Partial<ProductFeedbackFunnelValues>) => void;
  dict: ProductsDict | undefined;
  idPrefix?: string;
};

const STARS_ONLY = '__stars_only__';
const CUSTOM = '__custom__';

function questionsFromTemplate(
  questions: FeedbackQuestion[],
): ProductFeedbackQuestionDraft[] {
  return questions.slice(0, 5).map((q) => ({
    key: q.id,
    id: q.id,
    type: q.type,
    label: q.label,
    required: q.required,
    optionsText: (q.options ?? []).join('\n'),
  }));
}

function matchFormSource(
  drafts: ProductFeedbackQuestionDraft[],
  templates: FeedbackFormTemplate[],
  starsOnlyAllowed: boolean,
): string {
  if (drafts.length === 0) {
    return starsOnlyAllowed ? STARS_ONLY : CUSTOM;
  }

  const draftIds = drafts
    .map((q) => q.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);

  for (const template of templates) {
    const templateIds = template.questions.map((q) => q.id);
    if (
      draftIds.length === templateIds.length &&
      draftIds.length > 0 &&
      draftIds.every((id) => templateIds.includes(id))
    ) {
      return template.id;
    }
  }

  for (const template of templates) {
    const labels = template.questions.map((q) => q.label.trim());
    const draftLabels = drafts.map((q) => q.label.trim());
    if (
      labels.length === draftLabels.length &&
      labels.length > 0 &&
      labels.every((label, i) => label === draftLabels[i])
    ) {
      return template.id;
    }
  }

  return CUSTOM;
}

export function ProductFeedbackFunnelFields({
  values,
  onChange,
  dict,
  idPrefix = 'product-feedback',
}: ProductFeedbackFunnelFieldsProps) {
  const { isFeatureEnabled, isLoading } = useEntitlements();
  const feedbackEnabled = isFeatureEnabled('feature.feedback');
  const funnelDict = dict?.edit?.feedbackFunnel;
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { navigateShell } = useNavigateShell();
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(
    null,
  );

  const collectsFeedback =
    values.feedbackMode === FeedbackInviteMode.FEEDBACK_ONLY ||
    values.feedbackMode === FeedbackInviteMode.FEEDBACK_THEN_OPTIONAL_REVIEW;

  const starsOnlyAllowed =
    values.feedbackMode === FeedbackInviteMode.FEEDBACK_THEN_OPTIONAL_REVIEW;

  const { data: formTemplates = [], isLoading: templatesLoading } =
    useFeedbackFormTemplates(workspaceId, {
      enabled: feedbackEnabled && collectsFeedback,
    });

  const formSource = useMemo(
    () =>
      matchFormSource(
        values.feedbackQuestions,
        formTemplates,
        starsOnlyAllowed,
      ),
    [formTemplates, starsOnlyAllowed, values.feedbackQuestions],
  );

  if (pendingTemplateId) {
    const template = formTemplates.find((t) => t.id === pendingTemplateId);
    if (template) {
      setPendingTemplateId(null);
      onChange({
        feedbackQuestions: questionsFromTemplate(template.questions),
      });
    }
  }

  const autoFillKey = [
    feedbackEnabled,
    collectsFeedback,
    starsOnlyAllowed,
    values.feedbackQuestions.length,
    templatesLoading,
    formTemplates.length,
    formTemplates[0]?.id ?? '',
  ].join('\0');
  const [prevAutoFillKey, setPrevAutoFillKey] = useState('');
  if (
    autoFillKey !== prevAutoFillKey &&
    feedbackEnabled &&
    collectsFeedback &&
    !starsOnlyAllowed &&
    values.feedbackQuestions.length === 0 &&
    !templatesLoading
  ) {
    setPrevAutoFillKey(autoFillKey);
    if (formTemplates.length === 0) {
      queueMicrotask(() => setLibraryOpen(true));
    } else {
      const firstTemplate = formTemplates[0];
      if (firstTemplate) {
        onChange({
          feedbackQuestions: questionsFromTemplate(firstTemplate.questions),
        });
      }
    }
  } else if (autoFillKey !== prevAutoFillKey) {
    setPrevAutoFillKey(autoFillKey);
  }

  const setMode = (mode: FeedbackInviteMode) => {
    if (!feedbackEnabled && mode !== FeedbackInviteMode.DIRECT_REVIEW) {
      return;
    }
    const patch: Partial<ProductFeedbackFunnelValues> = { feedbackMode: mode };
    if (mode === FeedbackInviteMode.DIRECT_REVIEW) {
      patch.feedbackQuestions = [];
    } else if (
      mode === FeedbackInviteMode.FEEDBACK_ONLY &&
      values.feedbackQuestions.length === 0 &&
      formTemplates.length > 0
    ) {
      const firstTemplate = formTemplates[0];
      if (firstTemplate) {
        patch.feedbackQuestions = questionsFromTemplate(
          firstTemplate.questions,
        );
      }
    } else if (
      mode === FeedbackInviteMode.FEEDBACK_ONLY &&
      values.feedbackQuestions.length === 0
    ) {
      setLibraryOpen(true);
    }
    onChange(patch);
  };

  const applyFormSource = (next: string) => {
    if (next === STARS_ONLY) {
      if (!starsOnlyAllowed) return;
      onChange({ feedbackQuestions: [] });
      return;
    }
    if (next === CUSTOM) return;
    const template = formTemplates.find((t) => t.id === next);
    if (!template) return;
    onChange({ feedbackQuestions: questionsFromTemplate(template.questions) });
  };

  const sectionTitle = funnelDict?.title ?? 'Guest Feedback funnel';
  const modeLabel = funnelDict?.mode ?? 'Funnel mode';
  const thresholdLabel = funnelDict?.threshold ?? 'Review CTA threshold';
  const formLabel = funnelDict?.form ?? 'Feedback form';

  return (
    <>
      <SettingsSection
        id={idPrefix}
        title={sectionTitle}
        info={funnelDict?.subtitle}
        footer={
          !isLoading && !feedbackEnabled ? funnelDict?.freeHint : undefined
        }
        grouped
      >
        <SettingsGroupRows>
          {!isLoading && !feedbackEnabled ? (
            <SettingsGroupRow>
              <UpgradeCard feature="feature.feedback" compact />
            </SettingsGroupRow>
          ) : null}

          <SettingsGroupRow separated={!isLoading && !feedbackEnabled}>
            <div className="space-y-0.5 min-w-0 w-full">
              <div className="flex items-center gap-0.5">
                <Label htmlFor={`${idPrefix}-mode`}>{modeLabel}</Label>
                {funnelDict?.modeHelp ? (
                  <SettingsInfoHint label={modeLabel}>
                    {funnelDict.modeHelp}
                  </SettingsInfoHint>
                ) : null}
              </div>
              <Select
                value={values.feedbackMode}
                onValueChange={(v) => setMode(v as FeedbackInviteMode)}
                disabled={isLoading || !feedbackEnabled}
              >
                <SelectTrigger id={`${idPrefix}-mode`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FeedbackInviteMode.DIRECT_REVIEW}>
                    {funnelDict?.modes?.directReview ?? 'Direct Google review'}
                  </SelectItem>
                  <SelectItem
                    value={FeedbackInviteMode.FEEDBACK_ONLY}
                    disabled={!feedbackEnabled}
                  >
                    {funnelDict?.modes?.feedbackOnly ?? 'Feedback only'}
                  </SelectItem>
                  <SelectItem
                    value={FeedbackInviteMode.FEEDBACK_THEN_OPTIONAL_REVIEW}
                    disabled={!feedbackEnabled}
                  >
                    {funnelDict?.modes?.feedbackThenReview ??
                      'Feedback, then optional Google review'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </SettingsGroupRow>

          {collectsFeedback &&
          values.feedbackMode ===
            FeedbackInviteMode.FEEDBACK_THEN_OPTIONAL_REVIEW ? (
            <SettingsGroupRow separated>
              <div className="space-y-0.5 min-w-0 w-full">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={`${idPrefix}-threshold`}>
                    {thresholdLabel}
                  </Label>
                  {funnelDict?.thresholdHelp ? (
                    <SettingsInfoHint label={thresholdLabel}>
                      {funnelDict.thresholdHelp}
                    </SettingsInfoHint>
                  ) : null}
                </div>
                <Input
                  id={`${idPrefix}-threshold`}
                  type="number"
                  min={1}
                  max={5}
                  value={values.reviewThreshold}
                  disabled={!feedbackEnabled}
                  onChange={(e) =>
                    onChange({
                      reviewThreshold: Math.min(
                        5,
                        Math.max(1, Number(e.target.value) || 4),
                      ),
                    })
                  }
                />
              </div>
            </SettingsGroupRow>
          ) : null}

          {collectsFeedback && feedbackEnabled ? (
            <SettingsGroupRow separated>
              <div className="flex min-w-0 w-full flex-col gap-3">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-0.5">
                    <Label htmlFor={`${idPrefix}-form`}>{formLabel}</Label>
                    {funnelDict?.formHelp ? (
                      <SettingsInfoHint label={formLabel}>
                        {funnelDict.formHelp}
                      </SettingsInfoHint>
                    ) : null}
                  </div>
                </div>

                {templatesLoading ? (
                  <p className="text-sm text-muted-foreground">
                    {funnelDict?.formLoading ?? 'Loading forms…'}
                  </p>
                ) : formTemplates.length === 0 && formSource !== CUSTOM ? (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-muted-foreground">
                      {starsOnlyAllowed
                        ? (funnelDict?.noFormsYet ??
                          'No forms in your library yet. Use stars only, or add a form from the catalog.')
                        : (funnelDict?.noFormsRequired ??
                          'No forms in your library yet. Add a form from the catalog — Feedback only requires a form.')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {starsOnlyAllowed ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => applyFormSource(STARS_ONLY)}
                        >
                          {funnelDict?.starsOnly ?? 'Stars only'}
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => setLibraryOpen(true)}
                      >
                        {funnelDict?.fromLibrary ?? 'Add form from library'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Select
                    value={formSource === CUSTOM ? undefined : formSource}
                    onValueChange={applyFormSource}
                    disabled={!feedbackEnabled}
                  >
                    <SelectTrigger id={`${idPrefix}-form`}>
                      <SelectValue
                        placeholder={
                          funnelDict?.formPlaceholder ?? 'Choose a form…'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {starsOnlyAllowed ? (
                        <SelectItem value={STARS_ONLY}>
                          {funnelDict?.starsOnly ??
                            'Stars only (no extra questions)'}
                        </SelectItem>
                      ) : null}
                      {formTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                      {formSource === CUSTOM ? (
                        <SelectItem value={CUSTOM}>
                          {funnelDict?.customQuestions ??
                            'Custom questions (replace with a form)'}
                        </SelectItem>
                      ) : null}
                    </SelectContent>
                  </Select>
                )}

                {formSource === STARS_ONLY &&
                starsOnlyAllowed &&
                funnelDict?.starsOnlyGoogleHelp ? (
                  <SettingsInfoHint
                    label={funnelDict.starsOnly ?? 'Stars only'}
                  >
                    {funnelDict.starsOnlyGoogleHelp}
                  </SettingsInfoHint>
                ) : null}

                {formSource === CUSTOM && funnelDict?.customQuestionsHelp ? (
                  <SettingsInfoHint
                    label={funnelDict.customQuestions ?? 'Custom questions'}
                  >
                    {funnelDict.customQuestionsHelp}
                  </SettingsInfoHint>
                ) : null}

                {values.feedbackQuestions.length > 0 ? (
                  <ul className="space-y-1.5">
                    {values.feedbackQuestions.map((q, index) => (
                      <li
                        key={q.key}
                        className="truncate text-sm text-foreground"
                      >
                        <span className="text-muted-foreground">
                          {index + 1}.{' '}
                        </span>
                        {q.label.trim() ||
                          funnelDict?.untitledQuestion ||
                          'Untitled question'}
                      </li>
                    ))}
                  </ul>
                ) : null}

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setLibraryOpen(true)}
                  >
                    {funnelDict?.fromLibrary ?? 'Add form from library'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => navigateShell(shellPaths.feedbackLibrary)}
                  >
                    {funnelDict?.manageForms ?? 'Manage forms'}
                  </Button>
                </div>
              </div>
            </SettingsGroupRow>
          ) : null}
        </SettingsGroupRows>
      </SettingsSection>

      <FeedbackTemplateLibraryPicker
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        kind="form"
        onAdded={(templateId) => setPendingTemplateId(templateId)}
      />
    </>
  );
}
